import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { CheckCircle2, CreditCard, Wallet, Banknote, Lock } from "lucide-react";
import { formatPrice } from "@/lib/format";
import { useApp } from "@/context/AppContext";
import { useI18n } from "@/i18n/I18nProvider";

const FIELD = "h-11 w-full rounded-lg border border-border px-3 text-sm outline-none focus:border-brand";

export default function CheckoutPage() {
  const { t, currency } = useI18n();
  const { cart, cartSubtotal } = useApp();
  const [placed, setPlaced] = useState(false);
  const [payment, setPayment] = useState("card");

  if (cart.length === 0 && !placed) return <Navigate to="/cart" replace />;
  const cur = cart[0]?.currency || currency;

  if (placed) {
    return (
      <div className="mx-auto max-w-xl px-4 py-16 text-center">
        <span className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-success/10"><CheckCircle2 className="text-success" size={36} /></span>
        <h1 className="mt-5 font-display text-2xl font-bold text-ink">{t("order_confirmed")}</h1>
        <p className="mx-auto mt-3 max-w-md text-sm text-muted-foreground">{t("payment_note")}</p>
        <Link to="/" className="mt-8 inline-block rounded-full bg-ink px-6 py-3 text-sm font-semibold text-white">{t("go_home")}</Link>
      </div>
    );
  }

  const payMethods = [
    ["card", CreditCard, t("payment_method")],
    ["paypal", Wallet, "PayPal"],
    ["cod", Banknote, "Cash on delivery"],
  ];

  return (
    <div className="mx-auto max-w-7xl px-4 py-8">
      <h1 className="mb-6 font-display text-2xl font-bold text-ink sm:text-3xl">{t("checkout_title")}</h1>
      <form onSubmit={(e) => { e.preventDefault(); setPlaced(true); }} className="grid gap-8 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <section className="rounded-2xl border border-border p-5">
            <h2 className="mb-4 font-display text-lg font-bold text-ink">{t("contact_info")}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <input required placeholder={t("full_name")} className={FIELD} data-testid="co-name" />
              <input required type="email" placeholder={t("email")} className={FIELD} data-testid="co-email" />
              <input required placeholder={t("phone")} className={`${FIELD} sm:col-span-2`} data-testid="co-phone" />
            </div>
          </section>

          <section className="rounded-2xl border border-border p-5">
            <h2 className="mb-4 font-display text-lg font-bold text-ink">{t("shipping_address")}</h2>
            <div className="grid gap-3 sm:grid-cols-2">
              <input required placeholder={t("address_line")} className={`${FIELD} sm:col-span-2`} data-testid="co-address" />
              <input required placeholder={t("country")} className={FIELD} data-testid="co-country" />
              <input required placeholder={t("city")} className={FIELD} data-testid="co-city" />
              <input required placeholder={t("postal_code")} className={FIELD} data-testid="co-postal" />
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-muted-foreground">
              <input type="checkbox" defaultChecked className="h-4 w-4 accent-brand" /> {t("billing_same")}
            </label>
          </section>

          <section className="rounded-2xl border border-border p-5">
            <h2 className="mb-4 font-display text-lg font-bold text-ink">{t("payment_method")}</h2>
            <div className="space-y-2">
              {payMethods.map(([id, Icon, label]) => (
                <label key={id} className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition ${payment === id ? "border-brand bg-brand/5" : "border-border"}`}>
                  <input type="radio" name="pay" checked={payment === id} onChange={() => setPayment(id)} className="accent-brand" data-testid={`pay-${id}`} />
                  <Icon size={18} className="text-brand" /> <span className="font-medium">{label}</span>
                </label>
              ))}
            </div>
            <p className="mt-3 flex items-start gap-1.5 text-xs text-muted-foreground"><Lock size={13} className="mt-0.5" /> {t("payment_note")}</p>
          </section>

          <section className="rounded-2xl border border-border p-5">
            <h2 className="mb-3 font-display text-lg font-bold text-ink">{t("order_notes")}</h2>
            <textarea rows={3} className="w-full rounded-lg border border-border p-3 text-sm outline-none focus:border-brand" data-testid="co-notes" />
          </section>
        </div>

        <div>
          <div className="sticky top-28 rounded-2xl border border-border p-5">
            <h2 className="mb-4 font-display text-lg font-bold text-ink">{t("order_summary")}</h2>
            <div className="max-h-60 space-y-3 overflow-auto">
              {cart.map((l) => (
                <div key={l.key} className="flex items-center gap-3">
                  <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-secondary">
                    {l.image && <img src={l.image} alt={l.name} className="h-full w-full object-cover" />}
                    <span className="absolute -end-1 -top-1 grid h-5 w-5 place-items-center rounded-full bg-ink text-[10px] font-bold text-white">{l.qty}</span>
                  </div>
                  <span className="line-clamp-2 flex-1 text-xs">{l.name}</span>
                  <span className="text-sm font-semibold">{formatPrice((l.price || 0) * l.qty, l.currency)}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 space-y-2 border-t border-border pt-4 text-sm">
              <div className="flex justify-between"><span className="text-muted-foreground">{t("subtotal")}</span><span className="font-semibold">{formatPrice(cartSubtotal, cur)}</span></div>
              <div className="flex justify-between"><span className="text-muted-foreground">{t("shipping")}</span><span className="text-muted-foreground">{t("shipping_calc")}</span></div>
            </div>
            <div className="mt-4 flex justify-between border-t border-border pt-4">
              <span className="font-display font-bold text-ink">{t("estimated_total")}</span>
              <span className="font-display text-xl font-extrabold text-ink">{formatPrice(cartSubtotal, cur)}</span>
            </div>
            <button className="mt-5 h-12 w-full rounded-full bg-brand text-sm font-bold text-brand-foreground hover:brightness-105" data-testid="place-order-btn">{t("place_order")}</button>
          </div>
        </div>
      </form>
    </div>
  );
}
